'use client';

import { c as _c } from "react/compiler-runtime";
import type { CodeSessionEvent } from '@/lib/code-sessions/types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';
import { codeSessionOptions } from './queries';
import { SessionFeed } from './runtime/session-feed';
import { getCodeSessionController } from './session-controller';
import { isDevelopment } from '@/lib/env';

/** Local-only entry point for tests/perf/code-stream; see its README. */
export type CodeSessionBenchHook = {
  sessionId: string;
  /** Delivers one flushed batch, as the realtime buffer below would. */
  receive: (events: CodeSessionEvent[]) => void;
};
declare global {
  // eslint-disable-next-line ts/consistent-type-definitions
  interface Window {
    __codeSessionBench?: CodeSessionBenchHook;
  }
}

/** Mounted once by the /code layout; readers never open additional sockets. */
export function CodeSessionSync(t0) {
  const $ = _c(23);
  const {
    sessionId
  } = t0;
  const client = useQueryClient();
  let t1;
  if ($[0] !== sessionId) {
    t1 = codeSessionOptions(sessionId);
    $[0] = sessionId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  useQuery(t1);
  let t2;
  if ($[2] !== client || $[3] !== sessionId) {
    t2 = getCodeSessionController(client, sessionId);
    $[2] = client;
    $[3] = sessionId;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const controller = t2;
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const buffer = useRef(t3);
  const timer = useRef(undefined);
  let t4;
  if ($[6] !== controller) {
    const flush = () => {
      clearTimeout(timer.current);
      timer.current = undefined;
      const events = buffer.current;
      buffer.current = [];
      controller.receive(events);
    };
    t4 = events_0 => {
      if (!events_0.length) {
        return;
      }
      buffer.current.push(...events_0);
      if (events_0.some(_temp)) {
        flush();
      } else {
        if (timer.current === undefined) {
          timer.current = setTimeout(flush, 32);
        }
      }
    };
    $[6] = controller;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const receive = t4;
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      clearTimeout(timer.current);
      timer.current = undefined;
      buffer.current = [];
    };
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const dropHeld = t5;
  let t6;
  let t7;
  if ($[9] !== controller || $[10] !== sessionId) {
    t6 = () => {
      if (!isDevelopment()) {
        return;
      }
      const hook = {
        sessionId,
        receive: events_1 => controller.receive(events_1)
      };
      window.__codeSessionBench = hook;
      return () => {
        if (window.__codeSessionBench === hook) {
          window.__codeSessionBench = undefined;
        }
      };
    };
    t7 = [controller, sessionId];
    $[9] = controller;
    $[10] = sessionId;
    $[11] = t6;
    $[12] = t7;
  } else {
    t6 = $[11];
    t7 = $[12];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[13] !== controller) {
    t8 = () => {
      const reconnect = () => {
        controller.recover();
        controller.refresh();
      };
      window.addEventListener("online", reconnect);
      return () => {
        window.removeEventListener("online", reconnect);
        clearTimeout(timer.current);
        controller.receive(buffer.current);
        buffer.current = [];
        timer.current = undefined;
      };
    };
    t9 = [controller];
    $[13] = controller;
    $[14] = t8;
    $[15] = t9;
  } else {
    t8 = $[14];
    t9 = $[15];
  }
  useEffect(t8, t9);
  let t10;
  let t11;
  if ($[16] !== controller) {
    t10 = () => () => controller.reportConnection("live", null);
    t11 = [controller];
    $[16] = controller;
    $[17] = t10;
    $[18] = t11;
  } else {
    t10 = $[17];
    t11 = $[18];
  }
  useEffect(t10, t11);
  let t12;
  if ($[19] !== controller || $[20] !== receive || $[21] !== sessionId) {
    t12 = <SessionFeed sessionId={sessionId} controller={controller} receive={receive} dropHeld={dropHeld} />;
    $[19] = controller;
    $[20] = receive;
    $[21] = sessionId;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  return t12;
}
function _temp(event) {
  return event.kind !== "agent" || !event.type.endsWith(".delta");
}